/**
 * Favorites Drawer Web Component
 * Mirrors Dawn's cart-drawer pattern.
 * Fetches product data from the locale-aware Ajax API
 * ({rootUrl}products/{handle}.js) on open, so titles, URLs and the
 * drawer chrome follow the storefront language.
 */

class FavoritesDrawer extends HTMLElement {
  constructor() {
    super();
    this._isRendering = false;

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    this.querySelector("#FavoritesDrawer-Overlay")?.addEventListener(
      "click",
      this.close.bind(this),
    );
    this.bindHeaderIcon();
    this.updateCount();

    // Listen for changes from any favorite toggle (heart buttons on cards)
    document.addEventListener("favorites:changed", (e) => {
      this.updateCount();
      this.syncDrawerState();
    });

    // Open the drawer when a product is added to favorites
    document.addEventListener("favorites:added", () => {
      this.open();
    });
  }

  /* ─── Header Icon Binding ─── */

  bindHeaderIcon() {
    // Class-based: there are two openers (desktop header icons + mobile menu
    // drawer utility links), so an id selector would only ever bind the first.
    document.querySelectorAll(".header__icon--favorites").forEach((icon) => {
      icon.setAttribute("role", "button");
      icon.setAttribute("aria-haspopup", "dialog");
      icon.addEventListener("click", (e) => {
        e.preventDefault();
        this.openFromIcon(icon, e);
      });
      icon.addEventListener("keydown", (e) => {
        if (e.code.toUpperCase() === "SPACE") {
          e.preventDefault();
          this.openFromIcon(icon, e);
        }
      });
    });
  }

  openFromIcon(icon, event) {
    // If triggered from inside the mobile menu drawer, close the menu first.
    // closeMenuDrawer requires an elementToFocus or it silently returns.
    const headerDrawer = icon.closest("header-drawer");
    if (headerDrawer && typeof headerDrawer.closeMenuDrawer === "function") {
      const summary = headerDrawer.querySelector("summary");
      headerDrawer.closeMenuDrawer(event, summary || icon);
      // closeMenuDrawer only resets aria-expanded for keyboard events; without
      // this, the hamburger's [aria-expanded='true']::before page overlay
      // stays visible after the favorites drawer closes.
      summary?.setAttribute("aria-expanded", "false");
    }
    this.open(icon);
  }

  /* ─── Count Badge ─── */

  updateCount() {
    const count = Favorites.count();
    const bubbles = document.querySelectorAll(".favorites-count-bubble");
    bubbles.forEach((bubble) => {
      const countEl = bubble.querySelector("[data-favorites-count]");
      if (countEl) countEl.textContent = count;
      bubble.hidden = count === 0;
    });
  }

  /* ─── State Toggling (empty vs populated) ─── */

  showEmptyState() {
    const emptyEl = this.querySelector("[data-favorites-empty]");
    const popEl = this.querySelector("[data-favorites-populated]");
    if (emptyEl) emptyEl.style.display = "";
    if (popEl) popEl.style.display = "none";
  }

  showPopulatedState() {
    const emptyEl = this.querySelector("[data-favorites-empty]");
    const popEl = this.querySelector("[data-favorites-populated]");
    if (emptyEl) emptyEl.style.display = "none";
    if (popEl) popEl.style.display = "";
  }

  /**
   * Called on favorites:changed — handles removal without full re-render.
   * If drawer is open, remove the DOM node for any handle no longer in favorites.
   */
  syncDrawerState() {
    if (!this.classList.contains("active")) return;

    const handles = Favorites.getAll();
    const container = this.querySelector("#FavoritesDrawer-Items");

    if (!container) return;

    if (handles.length === 0) {
      container.innerHTML = "";
      this.showEmptyState();
      return;
    }

    // Remove DOM nodes for items no longer in favorites
    const itemEls = container.querySelectorAll(
      ".favorites-drawer__item:not(.favorites-drawer__item--skeleton)",
    );
    itemEls.forEach((el) => {
      const handle = el.dataset.productHandle;
      if (handle && !handles.includes(handle)) {
        el.style.transition = "opacity 0.2s ease, transform 0.2s ease";
        el.style.opacity = "0";
        el.style.transform = "translateX(20px)";
        setTimeout(() => {
          el.remove();
          if (
            container.querySelectorAll(
              ".favorites-drawer__item:not(.favorites-drawer__item--skeleton)",
            ).length === 0
          ) {
            this.showEmptyState();
          }
        }, 200);
      }
    });
  }

  /* ─── Open / Close ─── */

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    // Always show populated state with skeletons first, then render
    this.renderProducts();

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const focusElement =
          this.querySelector(".favorites-drawer__inner") ||
          this.querySelector(".favorites-drawer__close");
        if (typeof trapFocus === "function") {
          trapFocus(this.querySelector("#FavoritesDrawer"), focusElement);
        }
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove("overflow-hidden");
  }

  /* ─── Render Products (on drawer open) ─── */

  async renderProducts() {
    if (this._isRendering) return;
    this._isRendering = true;

    const handles = Favorites.getAll();
    const container = this.querySelector("#FavoritesDrawer-Items");

    if (handles.length === 0) {
      this.showEmptyState();
      if (container) container.innerHTML = "";
      this._isRendering = false;
      return;
    }

    // Show populated state immediately with skeleton loaders
    this.showPopulatedState();
    if (container) {
      container.innerHTML = handles.map(() => this.skeletonCard()).join("");
    }

    // Fetch all products in parallel via the locale-aware Ajax API
    // (e.g. /ar/products/{handle}.js returns translated titles).
    const results = await Promise.allSettled(
      handles.map((handle) =>
        fetch(`${this.rootUrl()}products/${handle}.js`).then((res) => {
          if (!res.ok) throw new Error(`Not found: ${handle}`);
          return res.json();
        }),
      ),
    );

    if (!container) {
      this._isRendering = false;
      return;
    }

    const cards = [];
    results.forEach((result, i) => {
      if (result.status === "fulfilled" && result.value) {
        cards.push(this.productCard(result.value));
      } else {
        // Product no longer exists — silently remove
        const h = handles[i];
        const stored = Favorites.getAll();
        const idx = stored.indexOf(h);
        if (idx > -1) {
          stored.splice(idx, 1);
          try {
            localStorage.setItem("shopify-favorites", JSON.stringify(stored));
          } catch (e) {}
        }
      }
    });

    if (cards.length === 0) {
      container.innerHTML = "";
      this.showEmptyState();
    } else {
      // Fade in the real cards
      container.innerHTML = cards.join("");
      // Trigger staggered fade-in animation
      const items = container.querySelectorAll(".favorites-drawer__item");
      items.forEach((item, index) => {
        item.style.opacity = "0";
        item.style.transform = "translateY(10px)";
        setTimeout(() => {
          item.style.transition = "opacity 0.3s ease, transform 0.3s ease";
          item.style.opacity = "1";
          item.style.transform = "translateY(0)";
        }, index * 80);
      });
      this.bindRemoveButtons();
    }

    this.updateCount();
    this._isRendering = false;
  }

  /* ─── Card Templates ─── */

  skeletonCard() {
    return `
      <div class="favorites-drawer__item favorites-drawer__item--skeleton">
        <div class="favorites-drawer__item-image skeleton"></div>
        <div class="favorites-drawer__item-details">
          <div class="skeleton skeleton--text" style="width:80%;height:1.4rem;"></div>
          <div class="skeleton skeleton--text" style="width:50%;height:1.2rem;margin-top:0.6rem;"></div>
          <div class="skeleton skeleton--text" style="width:35%;height:1rem;margin-top:0.8rem;"></div>
        </div>
      </div>
    `;
  }

  productCard(product) {
    // Ajax API shape: images are URL strings, prices are in the currency's subunits.
    const image =
      product.featured_image || (product.images && product.images[0]) || "";
    const imgTag = image
      ? `<img src="${this.resizeImage(image, "150x")}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="100" height="100">`
      : '<div class="favorites-drawer__no-image"></div>';

    const productUrl = `${this.rootUrl()}products/${this.escapeHtml(product.handle)}`;
    const variant = product.variants[0];
    const price = this.formatMoney(variant.price / 100);
    const comparePrice =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? this.formatMoney(variant.compare_at_price / 100)
        : "";
    const removeLabel = this.strings().remove.replace(
      "__TITLE__",
      this.escapeHtml(product.title),
    );

    return `
      <div class="favorites-drawer__item" data-product-handle="${this.escapeHtml(product.handle)}">
        <div class="favorites-drawer__item-image">
          <a href="${productUrl}" tabindex="-1">${imgTag}</a>
        </div>
        <div class="favorites-drawer__item-details">
        <div class="title-with-action">
          <a href="${productUrl}" class="favorites-drawer__item-title h4 break">
            ${this.escapeHtml(product.title)}
          </a>
          <div class="favorites-drawer__item-actions">
            <button class="favorites-drawer__remove" data-product-handle="${this.escapeHtml(product.handle)}" aria-label="${removeLabel}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20"><path d="M4.72 4.72a.75.75 0 0 1 1.06 0L10 8.94l4.22-4.22a.75.75 0 1 1 1.06 1.06L11.06 10l4.22 4.22a.75.75 0 1 1-1.06 1.06L10 11.06l-4.22 4.22a.75.75 0 0 1-1.06-1.06L8.94 10 4.72 5.78a.75.75 0 0 1 0-1.06Z"/></svg>
            </button>
          </div>
          </div>
          <div class="favorites-drawer__item-price">
            ${comparePrice ? `<s class="favorites-drawer__compare-price">${comparePrice}</s>` : ""}
            <span class="favorites-drawer__current-price${comparePrice ? " favorites-drawer__current-price--on-sale" : ""}">${price}</span>
          </div>

        </div>
      </div>
    `;
  }

  /* ─── Event Bindings ─── */

  bindRemoveButtons() {
    this.querySelectorAll(".favorites-drawer__remove").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const handle = e.currentTarget.dataset.productHandle;
        Favorites.remove(handle);
      });
    });
  }

  /* ─── Helpers ─── */

  rootUrl() {
    // routes.root_url is "/" on the primary locale but "/ar" (no trailing
    // slash) on others — normalize so path concatenation always works.
    const root =
      (window.__favoritesConfig && window.__favoritesConfig.rootUrl) || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  strings() {
    const defaults = {
      add: "Add __TITLE__ to favorites",
      remove: "Remove __TITLE__ from favorites",
    };
    return (
      (window.__favoritesConfig && window.__favoritesConfig.strings) || defaults
    );
  }

  resizeImage(src, size) {
    if (!src) return "";
    return src.replace(/(\.[^.]+)$/, `_${size}$1`);
  }

  escapeHtml(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Format a price given in MAJOR currency units (callers divide the Ajax
   * API's subunit price by 100 before passing it in).
   */
  formatMoney(price) {
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return price;

    const formatted = numericPrice.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    if (window.__shopMoneyFormat) {
      return window.__shopMoneyFormat
        .replace("{{amount}}", formatted)
        .replace("{{ amount }}", formatted)
        .replace(
          "{{amount_no_decimals}}",
          Math.round(numericPrice).toLocaleString(),
        )
        .replace(
          "{{ amount_no_decimals }}",
          Math.round(numericPrice).toLocaleString(),
        )
        .replace("{{amount_with_comma_separator}}", formatted)
        .replace("{{ amount_with_comma_separator }}", formatted);
    }

    if (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
    ) {
      return `${formatted} ${window.Shopify.currency.active}`;
    }

    return formatted;
  }
}

customElements.define("favorites-drawer", FavoritesDrawer);
